import type { Node, Edge } from '@xyflow/react'
import type { ProjectLabel } from '@bandos/shared'
import type { ReactFlowNodeData, CollapsedHierarchyNode } from '../types/mindmap-components'

/**
 * Function type for getting labels for a node
 */
export type GetNodeLabelsFunc = (nodeId: string) => ProjectLabel[]

/**
 * Build a hierarchical tree structure from a node and all its descendants.
 * Uses the edge traversal pattern where edge.source = parent, edge.target = child.
 *
 * @param rootNodeId - The ID of the root node to start from
 * @param nodes - All nodes in the mindmap
 * @param edges - All edges in the mindmap
 * @param getNodeLabels - Optional function to get labels for a node from context
 */
export function buildCollapsedHierarchy(
  rootNodeId: string,
  nodes: Node<ReactFlowNodeData>[],
  edges: Edge[],
  getNodeLabels?: GetNodeLabelsFunc
): CollapsedHierarchyNode {
  const node = nodes.find((n) => n.id === rootNodeId)
  if (!node) {
    throw new Error(`Node ${rootNodeId} not found`)
  }

  // Find direct children using edge.source = parent pattern
  const childEdges = edges.filter((edge) => edge.source === rootNodeId)
  const childNodes = childEdges
    .map((edge) => nodes.find((n) => n.id === edge.target))
    .filter((n): n is Node<ReactFlowNodeData> => n !== undefined)

  // Get labels - prefer from context function if provided, else from node data
  const labels = getNodeLabels ? getNodeLabels(rootNodeId) : node.data.labels

  return {
    id: node.id,
    label: node.data.label || '',
    category: node.data.category,
    children: childNodes.map((child) =>
      buildCollapsedHierarchy(child.id, nodes, edges, getNodeLabels)
    ),
    // Include additional display data based on node type
    stakeholderRating: node.data.stakeholderRating,
    phase: node.data.phase,
    // Include labels/tags on this node
    labels: labels,
  }
}

/**
 * Get all descendant node IDs (not including the root).
 * Uses recursive traversal with visited set to prevent infinite loops.
 */
export function getDescendantNodeIds(
  nodeId: string,
  edges: Edge[],
  visited: Set<string> = new Set()
): string[] {
  if (visited.has(nodeId)) return []
  visited.add(nodeId)

  const childEdges = edges.filter((edge) => edge.source === nodeId)
  const childIds = childEdges.map((edge) => edge.target)

  const allDescendants: string[] = []
  for (const childId of childIds) {
    allDescendants.push(childId)
    allDescendants.push(...getDescendantNodeIds(childId, edges, visited))
  }

  return allDescendants
}

/**
 * Get all ancestor node IDs (not including the node itself).
 * Walks up the tree via edge.target → edge.source until a root is reached.
 * Uses a visited set to prevent infinite loops in case of edge cycles.
 */
export function getAncestorNodeIds(
  nodeId: string,
  edges: Pick<Edge, 'source' | 'target'>[]
): string[] {
  const ancestors: string[] = []
  let currentId = nodeId
  const visited = new Set<string>()

  while (true) {
    if (visited.has(currentId)) break
    visited.add(currentId)
    const parentEdge = edges.find((e) => e.target === currentId)
    if (!parentEdge) break
    ancestors.push(parentEdge.source)
    currentId = parentEdge.source
  }

  return ancestors
}

/**
 * Get all edges that should be hidden when collapsing.
 * Hide edges where source OR target is a descendant (but not the root).
 * Keep edges coming into the root node visible.
 */
export function getEdgesToHide(
  rootNodeId: string,
  descendantIds: string[],
  edges: Edge[]
): string[] {
  const descendantSet = new Set(descendantIds)

  return edges
    .filter((edge) => {
      // Hide edges where source is a descendant
      if (descendantSet.has(edge.source)) return true
      // Hide edges where target is a descendant
      if (descendantSet.has(edge.target)) return true
      // Hide edges going out from the root to its children
      if (edge.source === rootNodeId && descendantSet.has(edge.target)) return true
      return false
    })
    .map((edge) => edge.id)
}

/**
 * Count total nodes in a collapsed hierarchy (including root).
 */
export function countHierarchyNodes(hierarchy: CollapsedHierarchyNode): number {
  return 1 + hierarchy.children.reduce((acc, child) => acc + countHierarchyNodes(child), 0)
}
